import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Save, CheckCircle2, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, StatusBadge } from "@/components/common";
import { useSettings } from "@/context/SettingsContext";
import { useFetch } from "@/hooks/useFetch";
import { api, formatApiError } from "@/lib/api";
import { fmtDate, money, todayISO } from "@/lib/format";
import { DEPOSIT_METHODS } from "@/lib/constants";
import { jobTypeNames } from "@/lib/jobTypes";

const Txt = ({ row, k, placeholder, set, type = "text", testId }) => (
  <Input value={row[k] ?? ""} onChange={(e) => set(k, e.target.value)} placeholder={placeholder} type={type} inputMode={type === "number" ? "decimal" : undefined}
    className="h-8 min-w-[130px] bg-amber-50/70 text-sm" data-testid={testId} />
);

const Pick = ({ row, k, options, placeholder, set, testId }) => (
  <Select value={row[k] || undefined} onValueChange={(v) => set(k, v)}>
    <SelectTrigger className="h-8 min-w-[150px] bg-amber-50/70 text-sm" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
  </Select>
);

const FIELD_FOR = {
  "Customer name": (p) => <Txt {...p} k="customer_name" placeholder="Customer name" testId={`fix-name-${p.row.id}`} />,
  "Mobile number": (p) => <Txt {...p} k="mobile" placeholder="04xx xxx xxx" testId={`fix-mobile-${p.row.id}`} />,
  "Job number": (p) => <Txt {...p} k="job_number" placeholder="Move Pro job #" testId={`fix-job-${p.row.id}`} />,
  "Pickup address": (p) => <Txt {...p} k="pickup_address" placeholder="Pickup address" testId={`fix-pickup-${p.row.id}`} />,
  "Delivery address": (p) => <Txt {...p} k="delivery_address" placeholder="Delivery address" testId={`fix-delivery-${p.row.id}`} />,
  "Job type": (p) => <Pick {...p} k="job_type" options={p.jobTypes} placeholder="Job type / route" testId={`fix-jobtype-${p.row.id}`} />,
  "Sales person": (p) => <Pick {...p} k="sales_person" options={p.salesPeople} placeholder="Sales person" testId={`fix-sales-${p.row.id}`} />,
  Deposit: (p) => (
    <div className="flex gap-1.5">
      <Txt {...p} k="deposit_amount" placeholder="Deposit $" type="number" testId={`fix-deposit-${p.row.id}`} />
      <Pick {...p} k="deposit_method" options={DEPOSIT_METHODS} placeholder="Method" testId={`fix-deposit-method-${p.row.id}`} />
    </div>
  ),
  "Hours worked": (p) => <Txt {...p} k="hours_worked" placeholder="Hours worked" type="number" testId={`fix-hours-${p.row.id}`} />,
  Price: (p) => p.row.pricing_type === "hourly" ? <div className="flex gap-1.5"><Txt {...p} k="hourly_rate" placeholder="Rate $/hr" type="number" testId={`fix-price-${p.row.id}`} /><Txt {...p} k="estimated_hours" placeholder="Est. hours" type="number" testId={`fix-hours-est-${p.row.id}`} /></div>
    : p.row.pricing_type === "cubic" ? <div className="flex gap-1.5"><Txt {...p} k="price_per_m3" placeholder="$ per m³" type="number" testId={`fix-price-${p.row.id}`} /><Txt {...p} k="cubic_metres" placeholder="m³" type="number" testId={`fix-cubic-${p.row.id}`} /></div>
    : <Txt {...p} k="fixed_price" placeholder="Fixed price $" type="number" testId={`fix-price-${p.row.id}`} />,
};

const KEYS = { "Customer name": ["customer_name"], "Mobile number": ["mobile"], "Job number": ["job_number"], "Pickup address": ["pickup_address"], "Delivery address": ["delivery_address"],
  "Job type": ["job_type"], "Sales person": ["sales_person"], Deposit: ["deposit_amount", "deposit_method"], "Hours worked": ["hours_worked"], Price: ["hourly_rate", "estimated_hours", "fixed_price", "price_per_m3", "cubic_metres"] };

const FixRow = ({ booking, jobTypes, salesPeople, onSaved }) => {
  const navigate = useNavigate();
  const [row, setRow] = useState(booking);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setRow((r) => ({ ...r, [k]: v }));
  const save = async () => {
    setBusy(true);
    const keys = booking.missing_info.flatMap((f) => KEYS[f] || []);
    const patch = Object.fromEntries(keys.filter((k) => row[k] !== booking[k]).map((k) => [k, row[k] === "" ? null : row[k]]));
    try {
      const { data } = await api.patch(`/bookings/${booking.id}/quick`, patch);
      toast.success(data.missing_info.length ? `Saved – still missing: ${data.missing_info.join(", ")}` : `${data.customer_name} – all details complete`);
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <tr className="border-t border-slate-100 align-top hover:bg-slate-50/60" data-testid={`fix-row-${booking.id}`}>
      <td className="whitespace-nowrap px-3 py-2.5 text-sm"><div className="font-medium">{fmtDate(booking.moving_date, "EEE d MMM")}</div><div className="text-xs text-slate-500">{booking.start_time || "—"}</div></td>
      <td className="px-3 py-2.5 text-sm">
        <button onClick={() => navigate(`/bookings/${booking.id}`)} className="font-medium text-sky-700 hover:underline" data-testid={`fix-open-${booking.id}`}>{booking.customer_name}</button>
        <div className="font-mono text-xs text-slate-500">{booking.job_number}{booking.bill_to_company ? ` · ${booking.bill_to_company}` : ""}</div>
        <div className="mt-1"><StatusBadge status={booking.status} /></div>
      </td>
      <td className="max-w-[260px] px-3 py-2.5 text-xs text-slate-500">{booking.pickup_address} → {booking.delivery_address}<div className="mt-0.5 font-mono text-slate-700">{money(booking.total_estimate)}</div></td>
      <td className="px-3 py-2.5">
        <div className="flex flex-wrap gap-2">
          {booking.missing_info.map((f) => FIELD_FOR[f] ? <div key={f}><div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">{f}</div>{FIELD_FOR[f]({ row, set, jobTypes, salesPeople })}</div> : null)}
        </div>
      </td>
      <td className="px-3 py-2.5 text-right">
        <Button size="sm" onClick={save} disabled={busy} data-testid={`fix-save-${booking.id}`} className="bg-[#0E1E38] text-white hover:bg-[#1a3058]">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</Button>
      </td>
    </tr>
  );
};

export default function FlaggedJobsPage() {
  const { settings } = useSettings();
  const [scope, setScope] = useState("upcoming");
  const { data, loading } = useFetch("/bookings", { flagged: "1", scope, today: todayISO() }, { poll: false });
  const [rows, setRows] = useState([]);
  const [done, setDone] = useState(0);
  useEffect(() => { setRows((data || []).filter((b) => !["Cancelled", "Refunded"].includes(b.status))); }, [data]);
  const onSaved = (b) => {
    if (b.missing_info.length === 0) { setRows((r) => r.filter((x) => x.id !== b.id)); setDone((d) => d + 1); }
    else setRows((r) => r.map((x) => (x.id === b.id ? b : x)));
  };
  return (
    <div data-testid="flagged-jobs-page">
      <PageHeader title="Fix flagged jobs" subtitle="Only the missing details are shown for each job. Fill them in and press Save – a job drops off this list once it's complete.">
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="h-9 w-[170px] bg-white" data-testid="fix-scope"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="upcoming">Upcoming jobs</SelectItem><SelectItem value="all">All jobs (incl. past)</SelectItem></SelectContent>
        </Select>
      </PageHeader>
      <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 font-semibold text-amber-900" data-testid="fix-remaining"><AlertTriangle className="h-3.5 w-3.5" /> {rows.length} job{rows.length === 1 ? "" : "s"} still missing info</span>
        {done > 0 && <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 font-semibold text-emerald-800" data-testid="fix-done"><CheckCircle2 className="h-3.5 w-3.5" /> {done} completed this session</span>}
      </div>
      {loading && !data ? <div className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /></div> : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-emerald-300 bg-emerald-50/50 py-16 text-center" data-testid="fix-empty"><CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-emerald-600" /><p className="font-semibold text-emerald-900">All {scope === "upcoming" ? "upcoming" : ""} jobs have complete information</p></div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full" data-testid="fix-table">
            <thead><tr className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">{["Date", "Job", "Route / price", "Missing details – fill in", ""].map((h) => <th key={h} className="px-3 py-2.5">{h}</th>)}</tr></thead>
            <tbody>{rows.map((b) => <FixRow key={b.id} booking={b} jobTypes={jobTypeNames(settings.job_types)} salesPeople={settings.sales_people} onSaved={onSaved} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
